import {Alert, H5, Icon, clx} from '@hm/fundamental'
import {mdiChevronDown, mdiDelete} from '@mdi/js'
import {useEffect, useState} from 'react'
import {Control, useFieldArray, useWatch, useFormState} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {LayerFormControl} from './LayerFormControl'

import {
  StaticPartLayerTypes,
  StaticPartsColors,
  StaticPartsColorsFaded
} from '@refra/modules/refra-static-parts/enums'
import {useEditReplacement} from '@refra/modules/refra-static-parts/providers'
import {ReplacementFormDTO} from '@refra/modules/refra-static-parts/types'

interface DetailSectionItemProps {
  materialInstallationIndex: number
  control: Control<ReplacementFormDTO>
  onDelete: () => void
}

export const DetailSectionItem = ({
  materialInstallationIndex,
  control,
  onDelete
}: DetailSectionItemProps) => {
  const {t} = useTranslation()
  const {
    activeMaterialInstallationIndex,
    setActiveMaterialInstallationIndex,
    replacementAreas,
    materialWeights
  } = useEditReplacement()
  const [isExpanded, setIsExpanded] = useState(
    materialInstallationIndex === activeMaterialInstallationIndex
  )

  const {errors, submitCount} = useFormState({control})
  const sectionIds = useWatch({
    control,
    name: `materialInstallations.${materialInstallationIndex}.sectionIds`
  })
  const hasSections = sectionIds && sectionIds.length > 0

  const {fields: layerFields} = useFieldArray({
    control,
    name: `materialInstallations.${materialInstallationIndex}.layers`
  })

  const workingLiningInstallationLayerIndex = layerFields.findIndex(
    (f) => f.layerType === StaticPartLayerTypes.TOP_LAYER
  )

  const watchedLayers = useWatch({
    control,
    name: `materialInstallations.${materialInstallationIndex}.layers`
  })

  const workingLiningRefractoryId =
    watchedLayers?.[workingLiningInstallationLayerIndex]?.refractoryId
  const hasWorkingLining = !!workingLiningRefractoryId

  const hasError =
    !!errors.materialInstallations?.[materialInstallationIndex] || !hasSections || !hasWorkingLining

  useEffect(() => {
    if (hasError && submitCount > 0) {
      setIsExpanded(true)
    }
  }, [hasError, submitCount])

  const isActive = materialInstallationIndex === activeMaterialInstallationIndex

  const color = StaticPartsColors[materialInstallationIndex % StaticPartsColors.length]
  const fadedColor =
    StaticPartsColorsFaded[materialInstallationIndex % StaticPartsColorsFaded.length]

  return (
    <div
      className={clx(
        'border rounded-lg overflow-visible transition-colors',
        isActive ? 'border-primary shadow-sm' : 'border-neutral_faded_20'
      )}
      onClick={() => setActiveMaterialInstallationIndex(materialInstallationIndex)}
    >
      <div
        className={clx(
          'flex items-center justify-between p-4',
          isActive && 'bg-primary_faded_10 rounded-lg'
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={clx('cursor-pointer transition-transform', isExpanded ? '' : '-rotate-90')}
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
          >
            <Icon path={mdiChevronDown} size="regular" />
          </div>
          <H5>
            {t('staticParts.editReplacement.sections.area', {
              index: materialInstallationIndex + 1
            })}
          </H5>
          <div
            className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{backgroundColor: fadedColor, color: color}}
          >
            {t('staticParts.editReplacement.sections.area', {
              index: materialInstallationIndex + 1
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <div className="px-2 py-0.5 rounded-md text-xs font-semibold bg-primary_toggled text-white">
              Selected
            </div>
          )}
          <div
            className="p-1 hover:bg-neutral_faded_20 rounded-md cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          >
            <Icon path={mdiDelete} className="text-neutral_faded_60" />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 flex flex-col gap-4 bg-white border-t border-neutral_faded_20 rounded-lg">
          {!hasSections && submitCount > 0 && (
            <Alert variant="error" data-cy={`no_sections_alert_${materialInstallationIndex}`}>
              {t('staticParts.editReplacement.errors.noSectionsSelected')}
            </Alert>
          )}
          {!hasWorkingLining && submitCount > 0 && (
            <Alert variant="error" data-cy={`no_working_layer_alert_${materialInstallationIndex}`}>
              {t('staticParts.editReplacement.errors.noWorkingLayer')}
            </Alert>
          )}
          <div className="flex flex-col gap-2">
            {layerFields.map((layerField, layerIndex) => (
              <LayerFormControl
                key={layerField.id}
                materialInstallationIndex={materialInstallationIndex}
                layerIndex={layerIndex}
                layerField={layerField}
                control={control}
                watchedLayer={watchedLayers?.[layerIndex]}
                area={replacementAreas[materialInstallationIndex] ?? 0}
                materialWeight={materialWeights[materialInstallationIndex]?.[layerIndex] ?? 0}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
